import { inject } from '@angular/core';
import { CanActivateFn, Router, Routes } from "@angular/router";
import { AboutComponent } from "./components/about-component/about-component";
import { HomeComponent } from "./components/home-component/home-component";
import { CharactersComponent } from "./components/game-layer/characters-component/characters-component";
import { CharacterCreateComponent } from "./components/game-layer/character-create-component/character-create-component";
import { CharacterInfoComponent } from "./components/game-layer/character-info-component/character-info-component";
import { CampaignInfoComponent } from "./components/game-layer/campaign-info-component/campaign-info-component";
import { UsersComponent } from "./components/users-component/users-component";
import { SessionComponent } from "./components/game-layer/session-component/session-component";
import { SessionCreateComponent } from "./components/game-layer/session-create-component/session-create-component";
import { UniverseComponent } from "./components/kb-layer/universe-component/universe-component";
import { LoginComponent } from "./components/login-component/login-component";
import { AuthService } from './services/auth-service';
import { UserRole } from './models/user';

const requireLoginGuard: CanActivateFn = () => {
    const auth = inject(AuthService);
    const router = inject(Router);

    if (!auth.currentUser()) {
        auth.restoreSession();
    }

    if (auth.isLoggedIn() && auth.hasSelectedRole()) {
        return true;
    }

    return router.createUrlTree(['/home']);
};

const requireRoleGuard = (...allowedRoles: UserRole[]): CanActivateFn => {
    return () => {
        const auth = inject(AuthService);
        const router = inject(Router);

        if (!auth.currentUser()) {
            auth.restoreSession();
        }

        if (!auth.isLoggedIn() || !auth.hasSelectedRole()) {
            return router.createUrlTree(['/home']);
        }

        const currentRole = auth.currentRole();
        if (currentRole && allowedRoles.includes(currentRole)) {
            return true;
        }

        return router.createUrlTree(['/home']);
    };
};


export const routes: Routes = [
    {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full'
    },
    {
        path: 'login',
        component: LoginComponent
    },
    {
        path: 'home',
        component: HomeComponent
    },
    {
        path: 'users',
        component: UsersComponent,
        canActivate: [requireRoleGuard(UserRole.Admin)]
    },
    {
        path: 'sessions/create',
        component: SessionCreateComponent,
        canActivate: [requireRoleGuard(UserRole.DungeonMaster)]
    },
    {
        path: 'sessions',
        component: SessionComponent,
        canActivate: [requireRoleGuard(UserRole.DungeonMaster, UserRole.Player)]
    },
    {
        path: 'universes',
        component: UniverseComponent,
        canActivate: [requireRoleGuard(UserRole.DungeonMaster)]
    },
    {
        path: 'characters',
        component: CharactersComponent,
        canActivate: [requireRoleGuard(UserRole.Player)]
    },
    {
        path: 'characters/create',
        component: CharacterCreateComponent,
        canActivate: [requireRoleGuard(UserRole.Player)]
    },
    {
        path: 'characters/:sessionId/:characterId',
        component: CharacterInfoComponent,
        canActivate: [requireRoleGuard(UserRole.DungeonMaster, UserRole.Player)]
    },
    {
        path: 'campaigns/:campaignId',
        component: CampaignInfoComponent,
        canActivate: [requireRoleGuard(UserRole.DungeonMaster, UserRole.Player)]
    },
    {
        path: 'about',
        component: AboutComponent
    }
];
